import React, { useState } from 'react';
import { Smartphone, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';

interface WalletButtonsProps {
  userId: string;
}

export const WalletButtons: React.FC<WalletButtonsProps> = () => {
  const [loading, setLoading] = useState<'apple' | 'google' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Detect platform
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const handleAddToWallet = async (platform: 'apple' | 'google') => {
    try {
      setLoading(platform);
      setError(null);

      console.log('[WalletButtons] Requesting wallet pass for platform:', platform);
      const response = await apiService.generateWalletPass(platform);

      console.log('[WalletButtons] Full response:', response);
      console.log('[WalletButtons] response.data:', response.data);

      if (response.error) {
        console.error('[WalletButtons] Error response:', response.error);
        throw new Error(response.error.message);
      }

      // Handle double-wrapped response format
      const responseData = (response.data as any)?.data || response.data;
      console.log('[WalletButtons] Extracted responseData:', responseData);
      
      const downloadUrl = responseData?.downloadUrl || responseData?.saveUrl;
      console.log('[WalletButtons] Extracted downloadUrl:', downloadUrl);

      if (!downloadUrl) {
        console.error('[WalletButtons] No URL found. responseData keys:', Object.keys(responseData || {}));
        throw new Error('No wallet pass URL returned from server');
      }

      console.log('[WalletButtons] Redirecting to:', downloadUrl);

      if (platform === 'apple') {
        // Download .pkpass file
        window.location.href = downloadUrl;
      } else {
        // Redirect to Google Wallet
        window.location.href = downloadUrl;
      }

    } catch (err: any) {
      console.error('Wallet pass generation error:', err);
      setError(err.message || 'Failed to generate wallet pass');
    } finally {
      setLoading(null);
    }
  };

  // Show appropriate button based on platform
  if (isIOS) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => handleAddToWallet('apple')}
          disabled={loading === 'apple'}
          className="w-full flex items-center justify-center space-x-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          {loading === 'apple' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Generating Pass...</span>
            </>
          ) : (
            <>
              <Smartphone className="h-5 w-5" />
              <span>Add to Apple Wallet</span>
            </>
          )}
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center">
          Your Pilot ID Card will be added to Apple Wallet for easy access
        </p>
      </div>
    );
  }

  if (isAndroid) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => handleAddToWallet('google')}
          disabled={loading === 'google'}
          className="w-full flex items-center justify-center space-x-2 bg-white text-gray-800 border-2 border-gray-300 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading === 'google' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Generating Pass...</span>
            </>
          ) : (
            <>
              <img 
                src="https://www.gstatic.com/images/branding/product/1x/wallet_48dp.png" 
                alt="Google Wallet"
                className="h-5 w-5"
              />
              <span>Add to Google Wallet</span>
            </>
          )}
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center">
          Your Pilot ID Card will be added to Google Wallet for easy access
        </p>
      </div>
    );
  }

  // Desktop - show both options with info
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Smartphone className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">
              Mobile Wallet Available
            </h4>
            <p className="text-sm text-blue-800">
              Open this page on your iPhone or Android device to add your Pilot ID Card to Apple Wallet or Google Wallet.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => handleAddToWallet('apple')}
          disabled={loading === 'apple'}
          className="flex flex-col items-center justify-center space-y-2 bg-black text-white px-4 py-4 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading === 'apple' ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <Smartphone className="h-6 w-6" />
              <span className="text-xs">Apple Wallet</span>
            </>
          )}
        </button>

        <button
          onClick={() => handleAddToWallet('google')}
          disabled={loading === 'google'}
          className="flex flex-col items-center justify-center space-y-2 bg-white text-gray-800 border-2 border-gray-300 px-4 py-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading === 'google' ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <img 
                src="https://www.gstatic.com/images/branding/product/1x/wallet_48dp.png" 
                alt="Google Wallet"
                className="h-6 w-6"
              />
              <span className="text-xs">Google Wallet</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        Download will start automatically. On mobile, the pass will be added to your wallet.
      </p>
    </div>
  );
};
